import React, { useState, useEffect } from 'react';
import { Target, Users, Award, Heart, Download } from 'lucide-react';
import { Button } from './ui/button';
import { getCompanyInfo } from '../data/api';

const About = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({ name: '', description: '' });

  useEffect(() => {
    getCompanyInfo().then(setCompanyInfo).catch(() => {});
  }, []);

  const handleDownloadProfile = async () => {
    setIsDownloading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/download/company-profile`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Yusen_Design_Company_Profile.pptx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setDownloadError(true);
      setTimeout(() => setDownloadError(false), 3000);
    } finally {
      setIsDownloading(false);
    }
  };

  const values = [
    {
      icon: Target,
      title: 'Our Philosophy',
      description: 'Delivering high-quality products and workmanship while creating comfortable and functional working environments.'
    },
    {
      icon: Users,
      title: 'Fine Craftsmanship',
      description: 'Upholding attention to detail and fine craftsmanship in every project we undertake.'
    },
    {
      icon: Award,
      title: 'Professional Service',
      description: 'Providing reliable, timely, and professional service that exceeds client expectations.'
    },
    {
      icon: Heart,
      title: 'Client Trust',
      description: 'Building long-term relationships through sustainable business practices and client satisfaction.'
    }
  ];

  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image Grid */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="aspect-square rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=400&h=400&fit=crop"
                    alt="Our Team"
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1600607687644-c7171b42498f?w=400&h=250&fit=crop"
                    alt="Design Process"
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                  />
                </div>
              </div>
              <div className="space-y-4 mt-8">
                <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?w=400&h=250&fit=crop"
                    alt="Materials"
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="aspect-square rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=400&fit=crop"
                    alt="Finished Project"
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                  />
                </div>
              </div>
            </div>

            {/* Decorative Badge */}
            <div className="absolute -bottom-6 -right-6 bg-blue-600 text-white p-8 rounded-2xl shadow-2xl">
              <div className="text-center">
                <div className="text-4xl font-bold">{companyInfo.established}</div>
                <div className="text-sm">Established</div>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                About Yusen Design & Contract
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed mb-4">
                Established in 1987 as an electrical engineering contractor, YUSEN steadily expanded into general renovation works and was incorporated as YUSEN Design & Contract Pte Ltd in 1990.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With decades of industry experience, we have grown into a full-service specialist in commercial and industrial interior design, renovation, graphic signage, and festive decorations. We are committed to delivering superior workmanship without compromising quality for cost.
              </p>
              <Button
                data-testid="download-profile-btn-about"
                onClick={handleDownloadProfile}
                disabled={isDownloading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 mt-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
              >
                <Download className="w-4 h-4 mr-2" />
                {isDownloading ? 'Downloading...' : 'Download Company Profile'}
              </Button>
            </div>

            <div className="bg-blue-50 rounded-xl p-6 border-l-4 border-blue-600">
              <h3 className="font-bold text-gray-900 mb-3">Our Specialized Divisions</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  'Carpentry',
                  'Electrical',
                  'General Works',
                  'Customer Service & Maintenance',
                  'Partition & Ceiling',
                  'Signage & Neon Lighting'
                ].map((division) => (
                  <div key={division} className="flex items-center gap-2 text-sm text-gray-700">
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    <span>{division}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Values Grid */}
            <div className="grid sm:grid-cols-2 gap-6 pt-8">
              {values.map((value) => {
                const IconComponent = value.icon;
                return (
                  <div key={value.title} className="space-y-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="font-bold text-gray-900">{value.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;